import React, { useState, useMemo } from "react";
import { useMode } from "../lib/mode";
import { SCENARIO_CHIPS } from "../lib/modeConfig";
import { getScenario } from "../lib/scenarios";
import { analyzeDecision, saveDecision } from "../lib/api";
import { ArrowRight, ArrowLeft, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["prompt", "context", "review"];

export default function DecisionWizard({ onComplete }) {
  const { mode, config, memberToken } = useMode();
  const [step, setStep] = useState(0);

  const [scenarioType, setScenarioType] = useState("M&A");
  const [decisionPrompt, setDecisionPrompt] = useState("");
  const [stakeholders, setStakeholders] = useState("");
  const [pressures, setPressures] = useState("");
  const [assumptions, setAssumptions] = useState("");
  const [timeline, setTimeline] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");

  // MBA-only
  const [email, setEmail] = useState("");
  const [teamCode, setTeamCode] = useState("");

  const [loading, setLoading] = useState(false);

  const scenario = useMemo(() => getScenario(scenarioType), [scenarioType]);
  const ph = scenario.context_placeholders;

  const loadExample = (ex) => {
    setDecisionPrompt(ex.prompt);
    setStakeholders(ex.context.stakeholders || "");
    setPressures(ex.context.pressures || "");
    setAssumptions(ex.context.assumptions || "");
    setTimeline(ex.context.timeline || "");
    setDesiredOutcome(ex.context.desired_outcome || "");
    toast.success(`Loaded example: ${ex.title}`);
  };

  const canNextFromPrompt = decisionPrompt.trim().length >= 8 && scenarioType;
  const canSubmit = canNextFromPrompt && (!config.askEmailAtEntry || (email.includes("@") && email.length > 4));

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Add a bit more about the decision before we begin.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        mode,
        scenario_type: scenarioType,
        decision_prompt: decisionPrompt.trim(),
        context: {
          stakeholders, pressures, assumptions, timeline, desired_outcome: desiredOutcome,
        },
        user_email: email || undefined,
        team_code: teamCode || undefined,
        member_token: memberToken || undefined,
      };
      const brief = await analyzeDecision(payload);

      let saved = null;
      // Auto-save for member and MBA modes. Public: don't auto-save (lead capture is separate)
      if (mode === "member" || mode === "mba") {
        try {
          saved = await saveDecision({ ...payload, brief });
        } catch (e) { /* non-blocking */ }
      }
      onComplete({ payload, brief, saved });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't generate the brief. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="decision-wizard">
      {/* Stepper */}
      <div className="flex items-center gap-3 mb-10 text-xs uppercase tracking-[0.18em] text-[#666666]">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-3">
            <span className={`h-1.5 w-8 rounded-full transition-colors ${i <= step ? "bg-[#2596be]" : "bg-[#E5E5E5]"}`} />
            <span className={i === step ? "text-[#1A1A1A]" : ""}>{["Prompt", "Context", "Review"][i]}</span>
            {i < STEPS.length - 1 && <span className="text-[#E5E5E5]">·</span>}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="pisa-enter">
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-3">Step 1</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-[#1A1A1A] mb-6 max-w-3xl">
            What decision are you trying to make?
          </h2>
          <p className="text-[#666666] mb-6 max-w-2xl">{config.intro}</p>

          <textarea
            value={decisionPrompt}
            onChange={(e) => setDecisionPrompt(e.target.value)}
            placeholder={scenario.prompt_placeholder}
            rows={5}
            className="w-full bg-[#F9F9F7] border border-[#E5E5E5] rounded-2xl p-5 md:p-6 text-base md:text-lg text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#2596be]/20 focus:border-[#2596be] transition-all resize-none"
            data-testid="decision-prompt-input"
          />

          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mt-8 mb-3">Scenario</p>
          <div className="flex flex-wrap gap-2" data-testid="scenario-chips">
            {SCENARIO_CHIPS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setScenarioType(c)}
                className={`pisa-chip inline-flex items-center px-4 py-2 rounded-full border text-sm transition-colors ${
                  scenarioType === c
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-white text-[#666666] border-[#E5E5E5] hover:border-[#2596be] hover:text-[#2596be]"
                }`}
                data-testid={`scenario-chip-${c.replace(/\W+/g, "-").toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>

          {scenario.examples && scenario.examples.length > 0 && (
            <div className="mt-8" data-testid="scenario-examples">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="h-3.5 w-3.5 text-[#f4b847]" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#666666]">Try an example for {scenarioType}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {scenario.examples.map((ex, i) => (
                  <button
                    key={`${scenarioType}-${i}`}
                    type="button"
                    onClick={() => loadExample(ex)}
                    className="text-left p-4 rounded-2xl bg-white border border-[#E5E5E5] hover:border-[#2596be] hover:shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all group"
                    data-testid={`example-${scenarioType.replace(/\W+/g, "-").toLowerCase()}-${i}`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#f4b847] mb-2 group-hover:text-[#e0a63b]">Example {i + 1}</p>
                    <p className="text-sm font-medium text-[#1A1A1A] mb-1.5">{ex.title}</p>
                    <p className="text-xs text-[#666666] line-clamp-3 leading-relaxed">{ex.prompt}</p>
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#666666] mt-3">Click any example to fill all the fields. You can edit them after.</p>
            </div>
          )}

          {config.askEmailAtEntry && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-2 block">Your email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@university.edu"
                  className="w-full px-5 py-3 rounded-full border border-[#E5E5E5] bg-[#F9F9F7] focus:outline-none focus:ring-2 focus:ring-[#2596be]/20 focus:border-[#2596be] transition-all"
                  data-testid="entry-email-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-2 block">Team code (optional)</label>
                <input
                  type="text"
                  value={teamCode}
                  onChange={(e) => setTeamCode(e.target.value)}
                  placeholder="e.g. ALPHA-3"
                  className="w-full px-5 py-3 rounded-full border border-[#E5E5E5] bg-[#F9F9F7] focus:outline-none focus:ring-2 focus:ring-[#2596be]/20 focus:border-[#2596be] transition-all"
                  data-testid="entry-team-input"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end mt-10">
            <button
              onClick={goNext}
              disabled={!canNextFromPrompt}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2596be] text-white font-medium hover:bg-[#1E7D9F] transition-colors disabled:opacity-50"
              data-testid="wizard-next-1"
            >
              Add context <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="pisa-enter">
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-3">Step 2</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-[#1A1A1A] mb-6 max-w-3xl">
            Who's in the room. What's pressing on you.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
            <Field label="Stakeholders" placeholder={ph.stakeholders} value={stakeholders} onChange={setStakeholders} testid="ctx-stakeholders" />
            <Field label="Pressures / constraints" placeholder={ph.pressures} value={pressures} onChange={setPressures} testid="ctx-pressures" />
            <Field label="Assumptions you're holding" placeholder={ph.assumptions} value={assumptions} onChange={setAssumptions} testid="ctx-assumptions" />
            <Field label="Timeline" placeholder={ph.timeline} value={timeline} onChange={setTimeline} testid="ctx-timeline" />
            <div className="md:col-span-2">
              <Field label="Desired outcome" placeholder={ph.desired_outcome} value={desiredOutcome} onChange={setDesiredOutcome} testid="ctx-outcome" />
            </div>
          </div>

          <div className="flex items-center justify-between mt-10">
            <button onClick={goBack} className="inline-flex items-center gap-2 text-[#666666] hover:text-[#1A1A1A] transition-colors" data-testid="wizard-back-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={goNext}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2596be] text-white font-medium hover:bg-[#1E7D9F] transition-colors"
              data-testid="wizard-next-2"
            >
              Review & run PISA <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="pisa-enter">
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-3">Step 3</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-[#1A1A1A] mb-6 max-w-3xl">
            Quick read before we run PISA.
          </h2>

          <div className="pisa-card p-7 md:p-9 space-y-5 max-w-3xl">
            <Row label="Scenario" value={scenarioType} />
            <Row label="Decision" value={decisionPrompt} multiline />
            {stakeholders && <Row label="Stakeholders" value={stakeholders} />}
            {pressures && <Row label="Pressures" value={pressures} />}
            {assumptions && <Row label="Assumptions" value={assumptions} />}
            {timeline && <Row label="Timeline" value={timeline} />}
            {desiredOutcome && <Row label="Desired outcome" value={desiredOutcome} />}
          </div>

          <div className="flex items-center justify-between mt-10">
            <button onClick={goBack} className="inline-flex items-center gap-2 text-[#666666] hover:text-[#1A1A1A] transition-colors" data-testid="wizard-back-3">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={submit}
              disabled={loading || !canSubmit}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1A1A1A] text-white font-medium hover:bg-black transition-colors disabled:opacity-60"
              data-testid="wizard-run-pisa"
            >
              {loading ? (
                <>
                  <span className="pisa-dot" />
                  <span className="pisa-dot" />
                  <span className="pisa-dot" />
                  <span className="ml-2">Pausing · Inspecting · Sensing · Aligning</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 text-[#f4b847]" />
                  Run PISA analysis
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, placeholder, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-2 block">{label}</label>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#F9F9F7] border border-[#E5E5E5] rounded-2xl p-4 text-sm md:text-base text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#2596be]/20 focus:border-[#2596be] transition-all resize-none"
        data-testid={testid}
      />
    </div>
  );
}

function Row({ label, value, multiline }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs uppercase tracking-[0.2em] text-[#666666]">{label}</span>
      <span className={`text-[#1A1A1A] ${multiline ? "leading-relaxed" : ""}`}>{value}</span>
    </div>
  );
}
